// C:\Projects\texe-web\src\pages\SearchUsers.tsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import UserListItem from "../components/UserListItem";

type TagHit = {
  id: string;
  author: { id: string; username: string; avatarUrlSmall?: string | null } | null;
  text: string;
  createdAt: string;
};

type SavWarTopic = {
  _id: string;
  tag: string;
  slug: string;
  totalSessions: number;
  lastUsedAt: string;
};

export default function SearchUsers() {
  const location = useLocation();
  const navigate = useNavigate();

  const qParam = useMemo(
    () => new URLSearchParams(location.search).get("q") || "",
    [location.search],
  );

  const [q, setQ] = useState(qParam);
  const [limit, setLimit] = useState(10);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<TagHit[]>([]);
  const [savwarTopics, setSavwarTopics] = useState<SavWarTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const debouncedQ = useDebounced(q, 300);
  const canLoadMore = limit < 25;

  useEffect(() => setQ(qParam), [qParam]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const s = debouncedQ.trim();
      if (!s) {
        setUsers([]);
        setTags([]);
        setSavwarTopics([]);
        setErr(null);
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        // Hashtag araması ise SavWar topic search kullan
        if (s.startsWith('#')) {
          const topicRes = await apiGet(`/api/savwar/topics/search?q=${encodeURIComponent(s)}`) as { success: boolean; data?: { topics?: SavWarTopic[] } };
          if (dead) return;
          setSavwarTopics(topicRes.data?.topics || []);
          setUsers([]);
          setTags([]);
        } else {
          // Normal kullanıcı araması
          const res = await apiGet(`/api/users/search?q=${encodeURIComponent(s)}&limit=${limit}`) as { items?: any[]; tags?: TagHit[] };
          if (dead) return;
          setUsers(res.items || []);
          setTags(res.tags || []);
          setSavwarTopics([]);
        }
      } catch (e: any) {
        if (!dead) setErr(e?.message || "Arama yapılamadı.");
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [debouncedQ, limit]);

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    const s = q.trim();
    navigate(`/search/users${s ? `?q=${encodeURIComponent(s)}` : ""}`);
  }

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <h2>Arama</h2>

      <form onSubmit={submit} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value.slice(0, 40))}
          placeholder="Kullanıcı veya #etiket"
          style={{ flex: 1, padding: 10, borderRadius: 8 }}
        />
        <button type="submit">Ara</button>
        <button
          type="button"
          onClick={() => {
            setQ("");
            setLimit(10);
            navigate("/search/users");
          }}
        >
          Temizle
        </button>
      </form>

      {loading && <div style={{ opacity: 0.8 }}>Yükleniyor…</div>}
      {err && <div style={{ color: "crimson" }}>{err}</div>}

      {!loading && !err && q.trim() && (
        <>
          {/* SavWar Topic sonuçları */}
          {savwarTopics.length > 0 && (
            <>
              <h3>⚔️ SavWar Konuları</h3>
              <ol style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 0 }}>
                {savwarTopics.map((topic) => (
                  <li
                    key={topic._id}
                    onClick={() => navigate(`/savwar/topics/${topic.slug}`)}
                    style={{
                      border: "2px solid #FFD700",
                      borderRadius: 12,
                      padding: 16,
                      listStyle: "none",
                      cursor: "pointer",
                      backgroundColor: "#1a1a1a",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#2a2a2a";
                      e.currentTarget.style.borderColor = "#FFF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#1a1a1a";
                      e.currentTarget.style.borderColor = "#FFD700";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ fontSize: 32 }}>⚔️</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 20, fontWeight: "bold", color: "#FFD700", marginBottom: 4 }}>
                          {topic.tag}
                        </div>
                        <div style={{ fontSize: 14, color: "#888" }}>
                          {topic.totalSessions} oturum · Son kullanım: {new Date(topic.lastUsedAt).toLocaleDateString("tr-TR")}
                        </div>
                      </div>
                      <div style={{ fontSize: 24, color: "#FFD700" }}>→</div>
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}

          {/* Kullanıcı sonuçları */}
          {users.length > 0 && (
            <>
              <h3>Kullanıcılar</h3>
              <ol style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 0 }}>
                {users.map((u) => (
                  <UserListItem key={u.id} u={u} />
                ))}
              </ol>
            </>
          )}

          {/* Etiket sonuçları */}
          {tags.length > 0 && (
            <>
              <h3>Etiketli Gönderiler</h3>
              <ol style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 0 }}>
                {tags.map((t) => (
                  <li
                    key={t.id}
                    style={{
                      border: "1px solid #333",
                      borderRadius: 8,
                      padding: 8,
                      listStyle: "none",
                    }}
                  >
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      @{t.author?.username ?? "Anon"} ·{" "}
                      <time dateTime={t.createdAt}>
                        {new Date(t.createdAt).toLocaleDateString("tr-TR")}
                      </time>
                    </div>
                    <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{t.text}</div>
                  </li>
                ))}
              </ol>
            </>
          )}

          {users.length === 0 && tags.length === 0 && savwarTopics.length === 0 && (
            <div style={{ opacity: 0.7 }}>Sonuç bulunamadı.</div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button
              onClick={() => setLimit((v) => Math.min(25, v + 5))}
              disabled={!canLoadMore || !q.trim()}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #2c2c2c",
                opacity: canLoadMore && q.trim() ? 0.95 : 0.4,
                cursor: canLoadMore && q.trim() ? "pointer" : "not-allowed",
              }}
            >
              Daha fazla yükle
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
